import { cloneDeep, each, remove } from "lodash";
import { EnquiryConfig, initEnquiryForm } from "./enquiry-form";
import { initSearchSuggestionInput } from "./search-suggestions";

const ENABLE_KEYWORD_SUGGESTIONS = false;

declare const jQuery: any;
const $ = jQuery;

const ajaxCall: Function = window["iw"].utils.ajaxCall;
const scrollToWithHeaderOffset: Function = window["iw"].layout.scrollToWithHeaderOffset;

type PostType = "product"|"supplier"|"event";

interface UpdateConfig
{
    postType: PostType;
    offset: number;
    cat?: number;
    subcat?: number;
    initial?: string;
    country?: number;
    keyword?: string;
    orderBy?: string;
    order?: string;
    eventStartDateRange?: string;
}

interface UpdateResult
{
    ok: boolean;
    nextOffset: number; // -1 if no next offset
    totalItems: number;
    itemsHtml: number; // to be added to items container, not replace it
}

export function initListingPageInputs(): void
{
    const $filterWraps = $(`[data-listing-page-filter-bar]`);
    if ($filterWraps.length === 0)
    {
        return;
    }

    const $enquiryPopup = $(`[data-popup="enquiry"]`);
    const inputNames: string[] = ["cat", "subcat", "initial", "country", "keyword", "eventStartDateRange"];
    const $resultWraps = $(`[data-listing-page-filter-results]`);

    const updatingWraps: HTMLElement[] = [];
    const setWrapUpdating = (wrap: HTMLElement, isUpdating: boolean) => {
        if (isUpdating)
        {
            updatingWraps.push(wrap);
        }
        else
        {
            remove(updatingWraps, (el: HTMLElement) => el === wrap);
        }
    };
    const wrapIsUpdating = (wrap: HTMLElement): boolean => updatingWraps.indexOf(wrap) >= 0;

    const updateResultSummary = ($resultWrap, $itemsWrap, result) => {
        const numItems: number = $itemsWrap.find(`[data-item]`).length;
        if (numItems === 0)
        {
            $resultWrap.addClass("empty");
            return;
        }
        const total: number = result.totalItems;
        const $summary = $resultWrap.find(`[data-pag-summary]`);
        let html: string;
        if (total === 1)
        {
            html = `1 result`;
        }
        else if (numItems === total)
        {
            html = `${total} results`;
        }
        else
        {
            html = `${total} results (1-${numItems})`;
        }
        $summary.html(html);
    };

    // Updates the post item cards with fresh server data
    // Returns success bool
    const updateResults = async (
        updateConfig: UpdateConfig,
        clearPrevious: boolean,
        $resultWrap,
        $loadMoreBtn,
        defaultCatId: number,
        catName: string): Promise<boolean> => {
        const wrap: HTMLElement = $resultWrap[0];
        if (wrapIsUpdating(wrap))
        {
            return false;
        }
        setWrapUpdating(wrap, true);
        updateConfig = cloneDeep(updateConfig);
        if (!updateConfig.cat)
        {
            updateConfig.cat = defaultCatId || undefined;
        }
        const $itemsWrap = $resultWrap.find(`[data-items]`);
        if (clearPrevious)
        {
            $itemsWrap.empty();
            $resultWrap.addClass("busy");
        }
        $resultWrap.css({ height: $resultWrap.outerHeight() }).removeClass("error empty");
        $loadMoreBtn.addClass("busy-button");
        let result: UpdateResult;
        try
        {
            result = await ajaxCall("hs_fetch_listing_page_results", updateConfig);
            if (!result || !result.ok)
            {
                console.error(`Fetch error result:`, result);
                throw new Error(`No result/result.ok in fetch result obj`);
            }
        }
        catch (error)
        {
            $resultWrap.removeClass("busy empty").addClass("error");
            $resultWrap.find("[data-reload]").off("click").on("click", () => {
                updateResults(updateConfig, clearPrevious, $resultWrap, $loadMoreBtn, defaultCatId, catName);
            });
            scrollToWithHeaderOffset($resultWrap);
            console.error("Fetch listing page posts failed", error);
            return false;
        }
        finally
        {
            setWrapUpdating(wrap, false);
            $resultWrap.css({ height: "" });
            $loadMoreBtn.removeClass("busy-button");
        }
        $itemsWrap.append(result.itemsHtml);
        $resultWrap.removeClass("busy error empty");
        if (result.nextOffset < 0)
        {
            $loadMoreBtn.css({ display: "none" });
        }
        else
        {
            $loadMoreBtn.css({ display: "" }).attr("data-load-more", result.nextOffset.toString());
        }
        initPostCardEnquireButtons($itemsWrap, $enquiryPopup, updateConfig.cat || defaultCatId, catName);
        updateResultSummary($resultWrap, $itemsWrap, result);
        return true;
    };

    // Set up each filter wrap (one for products, one for suppliers)
    $filterWraps.each(function () {
        const $filterWrap = $(this);
        const postType: PostType = $filterWrap.attr("data-listing-page-filter-bar");
        const $resultWrap = $resultWraps.filter(`[data-listing-page-filter-results="${postType}"]`).first();
        
        if ($resultWrap.length === 0)
        {
            console.error(`Failed to find result wrap for filter bar type "${postType}"`);
            return;
        }

        const defaultCatId: number = parseInt($filterWrap.attr("data-cat"), 10) || undefined;

        const catName: string = $resultWrap.attr("data-cat-name") || "";

        const updateConfig: UpdateConfig = {
            postType,
            offset: 0,
            order: $resultWrap.attr("data-result-order") || undefined,
            orderBy: $resultWrap.attr("data-result-order-by") || undefined,
        };

        // Input on change
        let inputOnChangeTimer: any = undefined;
        const $inputs = $filterWrap.find(`[data-input]`);
        each(inputNames, (inputName: string) => {
            $inputs.filter(`[data-input="${inputName}"]`).on("change", function onChange(event, value: string) {
                clearTimeout(inputOnChangeTimer);
                updateConfig.offset = 0;
                updateConfig[inputName] = value;
                inputOnChangeTimer = setTimeout(() => {
                    updateResults(updateConfig, true, $resultWrap, $loadMoreBtn, defaultCatId, catName);
                }, 60);
            }).trigger("update", "");
        });

        // Load more button
        const $loadMoreBtn = $resultWrap.find(`[data-load-more]`);
        $loadMoreBtn.on("click", function () {
            clearTimeout(inputOnChangeTimer);
            updateConfig.offset = parseInt($loadMoreBtn.attr("data-load-more"), 10) || 0;
            updateResults(updateConfig, false, $resultWrap, $loadMoreBtn, defaultCatId, catName);
        });

        // Submit button
        $inputs.find(`[data-submit-btn]`).on("click", function (event) {
            clearTimeout(inputOnChangeTimer);
            event.stopPropagation();
            inputOnChangeTimer = setTimeout(() => {
                updateResults(updateConfig, true, $resultWrap, $loadMoreBtn, defaultCatId, catName);
            }, 60);
            return false;
        });

        // Enquiry button
        $resultWrap.find(`[data-enquire]`).off("click").on("click", function (event): false {
            if ($enquiryPopup.length === 0)
            {
                console.error(`No enquiry popup found for [data-enquire] btn`);
                return false;
            }
            const postTypePlural: string = postType + "s";
            const defaultEnquiryText: string = `Enquiry about ${catName} ${postTypePlural}: `;
            const config = cloneDeep(updateConfig);
            config.cat = defaultCatId;
            setupEnquiryPopupSubmit($enquiryPopup, config, defaultEnquiryText, catName);
            $enquiryPopup.trigger("open");
            return false;
        });

        syncCategoryWithSubCategoryInputs($inputs);

        // Initial fetch
        updateResults(updateConfig, true, $resultWrap, $loadMoreBtn, defaultCatId, catName);

        // Suggestions in keyword input
        initKeywordInputSuggestions($inputs);
    });
}

function initKeywordInputSuggestions($inputWraps): void {
    if (!ENABLE_KEYWORD_SUGGESTIONS) {
        return;
    }
    const $wrap = $inputWraps.filter(`[data-input="keyword"]`);
    if ($wrap.length === 0) {
        return;
    }
    const $input = $wrap.find("input");
    const onSelectSuggestion = () => {
        if ($.trim($input.val())) {
            $input.trigger("change");
        }
    };
    initSearchSuggestionInput($wrap, $input, onSelectSuggestion);
}

function syncCategoryWithSubCategoryInputs($inputs): void {
    const $catWrap = $inputs.filter(`[data-input="cat"]`);
    const $subcatWrap = $inputs.filter(`[data-input="subcat"][data-meta]`);
    if ($catWrap.length === 0 || $subcatWrap.length === 0)
    {
        return;
    }
    let meta: any;
    try
    {
        meta = JSON.parse($subcatWrap.attr("data-meta"));
        if (!meta || !meta.filter_by_cat)
        {
            throw new Error(`no meta.filter_by_cat`);
        }
    }
    catch (error)
    {
        console.error(error);
        return;
    }
    const $cat = $catWrap.find(`select`);
    const $subcat = $subcatWrap.find(`select`);
    const subcatData = meta.filter_by_cat;
    const emptyOptions = () => {
        $subcat.find(`option`).filter(function () {
            return !!this.value;
        }).remove();
    };
    const itemToOption = (item) => {
        const $option = $(`<option value="${item.value}"></option>`).text(item.label).attr("data-option-title", item.label);
        if (item["class"])
        {
            $option.attr("data-option-class", item["class"]);
        }
        return $option;
    };
    const replace = (subcatItems: any[]) => {
        emptyOptions();
        each(subcatItems, (item) => {
            $subcat.append(itemToOption(item));
        });
    };
    const sync = () => {
        const id = $cat.val();
        const subcatItems: any[] = id ? (subcatData[id] || []) : [];
        replace(subcatItems);
        $subcat.val("").trigger("options-changed").trigger("change");
        $subcatWrap.trigger("change");
    };
    sync();
    $cat.on("change", sync);
}

function initPostCardEnquireButtons($itemsWrap, $enquiryPopup, catId: number, catNameForTemplateText: string): void
{
    $itemsWrap.find(`[data-post-id]`).each(function (): void {
        const $wrap = $(this);
        const postId: number = parseInt($wrap.attr("data-post-id"), 10);
        if (!postId)
        {
            console.error(`data-post-id missing on post card`, this);
            return;
        }
        const $enquiryBtn = $wrap.find(`.enquiry-button`);
        if ($enquiryBtn.length === 0)
        {
            return;
        }
        if ($enquiryPopup.length === 0)
        {
            console.error(`No enquiry popup found for post item enquiry buttons`);
            return;
        }
        const postTitle: string = $wrap.attr("data-post-title");
        const postType: PostType = $wrap.attr("data-post-type");
        const defaultEnquiryText: string = `Enquiry about ${postTitle}: `;
        const config: EnquiryConfig = { postId, postType, cat: catId };
        $enquiryBtn.off("click").on("click", function (event): false {
            event.preventDefault();
            event.stopPropagation();
            setupEnquiryPopupSubmit($enquiryPopup, config, defaultEnquiryText, catNameForTemplateText);
            $enquiryPopup.trigger("open");
            return false;
        });
    });
}

function setupEnquiryPopupSubmit($enquiryPopup, config: EnquiryConfig, defaultEnquiryText: string, catNameForTemplateText: string): void
{
    const $formWrap = $enquiryPopup.find(`.hs-enquiry-form`);
    $formWrap.off("success complete error submit").on("success", (event) => {
        $enquiryPopup.removeClass("busy");
        const timer = setTimeout(() => {
            $enquiryPopup.trigger("close");
        }, 5000);
        $enquiryPopup.one("close", () => {
            clearTimeout(timer);
        });
    }).on("complete", (event) => {
        $enquiryPopup.removeClass("busy");
    }).on("error", (event) => {
        $enquiryPopup.removeClass("busy").addClass("error");
    }).on("submit", (event) => {
        $enquiryPopup.addClass("busy");
    });
    initEnquiryForm($formWrap, config, defaultEnquiryText, catNameForTemplateText);
}
